import { Button } from '@askable/ui/core/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@askable/ui/core/dialog';
import { toast } from '@askable/ui/core/sonner';
import _ from 'lodash';
import { X } from 'lucide-react';
import { useState } from 'react';
import { useMutation } from 'urql';

import { LoadingOverlay } from 'components/common';
import { ReportIssue } from 'containers/Booking/containers/BookingParticipantsContainer/components/BookingParticipantsSidePanel/queries/ReportIssue.mutation';
import { ReportReason, ReportRequest } from 'src/constants/graphql-enums';

import './noShowStyles.scss';

function NoShowDialog(props: any) {
  const [state, setState] = useState({
    loading: false,
    confirmedAction: 0,
  });

  const [, reportIssue] = useMutation(ReportIssue);

  const onNoShowConfirmed = (value: number) => {
    setState({ ...state, loading: true });

    void reportIssue({
      bookingId: props.booking._id,
      bookingSubmissionId: props.participant.Submission?._id,
      reportReason: ReportReason.ParticipantNoShow,
      reportRequest: value === 1 ? ReportRequest.Refund : ReportRequest.Replace,
    }).then(result => {
      if (result.error) {
        setState({
          ...state,
          loading: false,
        });
        toast.error('Something went wrong. Please try again.');
        return;
      }
      setState({
        ...state,
        loading: false,
        confirmedAction: value,
      });
      if (props.onConfirmNoShow) props.onConfirmNoShow();
    });
  };

  const onCloseModal = () => {
    setState({
      ...state,
      loading: false,
      confirmedAction: 0,
    });
    props.onClose();
  };

  const refundCredits = (booking: any) => {
    // round the number of credits up to the nearest whole number
    const amount = Math.ceil(_.get(booking, 'total_amount.creditsPerParticipant', 1));
    return amount === 1 ? '1 credit' : `${amount} credits`;
  };

  const renderConfirmationMessage = () => {
    // 1 -> Refund
    // 2 -> Replace
    if (state.confirmedAction === 2) {
      return (
        <>
          <h2>Okay, we&apos;ve opened up another spot for this booking</h2>
          <p>
            You can now invite another participant as a replacement. You can also add additional session times by
            clicking on the calendar.
          </p>
        </>
      );
    }
    return (
      <>
        <h2>That&apos;s done</h2>
        <p>Askable automatically added {refundCredits(props.booking)} back to your account.</p>
      </>
    );
  };

  return (
    <Dialog open={props.open} modal={!!state.confirmedAction} onOpenChange={open => !open && onCloseModal()}>
      <DialogContent className="noShowDialogComponent" aria-describedby={undefined}>
        <DialogHeader>
          <DialogTitle className="sr-only">Participant no-show</DialogTitle>
        </DialogHeader>
        <div className="content text-pretty text-sm text-muted-foreground">
          {state.loading && <LoadingOverlay style={{ opacity: 0.8 }} />}
          {!state.confirmedAction ? (
            <>
              <X onClick={onCloseModal} className="defaultCloseIcon" style={{ width: 20, height: 20 }} />
              <div className="topContainer">
                <img className="illustration" src="/icons/noShowSadMail.png" alt="Sad mail" />
                <div className="contentTop">
                  <h2>Bummer :(</h2>
                  <p>
                    We're sorry to hear that <strong>{_.get(props.participant, 'user.meta.identity.firstname')}</strong>{' '}
                    didn't show up. You can choose one of the options below:
                  </p>
                </div>
              </div>
              <div className="actionContainer">
                <div className="replacement">
                  <div className="internalContent">
                    <h3>Replacement person</h3>
                    <p>
                      We'll open up a spot for you on this study and you'll be able to invite a different person, free
                      of charge.
                    </p>
                  </div>
                  <Button onClick={() => onNoShowConfirmed(2)} className="max-w-[150px]" variant="primary">
                    Replace
                  </Button>
                </div>
                <div className="refund">
                  <div className="internalContent">
                    <h3>Refund credits</h3>
                    <p>
                      Since you paid with credits, we'll add {refundCredits(props.booking)} back into your account
                      immediately.
                    </p>
                  </div>
                  <Button onClick={() => onNoShowConfirmed(1)} className="max-w-[150px]" variant="primary">
                    Refund
                  </Button>
                </div>
              </div>
            </>
          ) : (
            <div className="confirmationMessageContainer">
              {renderConfirmationMessage()}
              <Button
                variant="primary"
                onClick={() => {
                  props.onClickDone();
                }}
                className="float-right mt-5 max-w-[150px]"
                size="default"
              >
                Done
              </Button>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default NoShowDialog;
